import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { authSelectors } from 'app-components/flux/auth';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { useFetchOnce } from 'app-components/hooks/utils';
import { getEnvironmentConfig, getHostConfig, localPreferences } from 'app-components/utils/core';

import { getShareAcceptData, removeShareAcceptData, setShareAcceptData } from 'data/app/helpers';
import { useGlobalAuthPath, useGlobalSessionMismatch, useSharedSessionState } from './hooks';

const useAuthURL = () => {
  const [, setSharedSessionState] = useSharedSessionState();
  const [sessionMismatch] = useGlobalSessionMismatch();
  const [globalAuthPath] = useGlobalAuthPath();

  const { accessToken } = useSelector(authSelectors.getAuthSession) || {};
  const { username: login } = useSelector(profileSelectors.getProfile) || {};
  const dm = useSelector(authSelectors.getAuthTheme);
  useFetchOnce(profileActions.getProfile, profileSelectors, false, true);

  const authURL = useMemo(() => {
    if (!accessToken) {
      const newSessionState = uuidv4();
      // for no storage scenario
      setSharedSessionState(newSessionState);
      // for local storage passwordless login link scenario
      let sessionStates = [];
      try {
        sessionStates = localStorage.getItem('sessionStates');
        sessionStates = JSON.parse(sessionStates || '[]');
        sessionStates = Array.isArray(sessionStates) ? sessionStates : [];
      } catch (e) {
        assert(false, e);
      } finally {
        sessionStates.push(newSessionState);
        localStorage.setItem('sessionStates', JSON.stringify(sessionStates.slice(-100)));
      }

      const url = new URL(globalAuthPath, getEnvironmentConfig().signin_url);
      url.searchParams.set('client_id', getEnvironmentConfig().client_id);
      url.searchParams.set('response_type', 'code');
      url.searchParams.set('redirect_uri', getHostConfig()?.redirect_uri);
      url.searchParams.set('dm', dm);
      if (login) {
        url.searchParams.set('sign-in-only', '1');
        url.searchParams.set('quicken-id', login);
        url.searchParams.set('lock-id', '1');
      }
      if (newSessionState) {
        url.searchParams.set('session_state', btoa(newSessionState));
      }
      if (sessionMismatch) {
        url.searchParams.set('session_mismatch', '1');
      }

      const logoutReason = localPreferences.getLogoutReason();
      switch (logoutReason) {
        case 'AUTH_LOGOUT_EXPIRED':
          url.searchParams.set('expired', '1');
          break;
        case 'AUTH_LOGOUT_INACTIVE':
          url.searchParams.set('inactive', '1');
          break;
        default:
      }
      localPreferences.setLogoutReason(null);

      const keepLoggedIn = localPreferences.getKeepLoggedIn();
      if (keepLoggedIn) {
        url.searchParams.set('remember_me', '1');
      }

      const shareAcceptData = getShareAcceptData();
      if (shareAcceptData) {
        // If we have already tried to use the share data, then ignore and clear it. We allow more than 1 use for the
        // following reasons:
        //   1) Case where logging out existing user causes this path to execute twice but only redirecting to the
        //      authentication widget the second time. If we just allowed once, widget would not get required data.
        //   2) If user fails to complete authentication, then we will allow them to try at least once, possibly
        //      twice before clearing data.
        //   Note: When we successfully get an auth-code, we will also clear the share data.
        //
        if (shareAcceptData.used <= 3) {
          if (shareAcceptData.isGranteeNewUser) {
            url.searchParams.set('new', '1');
          }
          url.searchParams.set('granteeEmail', encodeURIComponent(shareAcceptData.granteeEmail));
          url.searchParams.set('datasetId', shareAcceptData.datasetId);
          setShareAcceptData(shareAcceptData.set('used', shareAcceptData.used + 1));
        } else {
          // if we are re-authenticating and not using the share data, then remove it
          removeShareAcceptData();
        }
      }

      const demoApp = getHostConfig()?.demo_app;
      if (demoApp) {
        url.searchParams.set('demo_app', demoApp);
      }

      // TODO: extend stub support to Amplify / non-localhost builds.
      // The mechanism would route sign-in through localhost:3000/auth-start
      // (a navigation hop, since HTTPS pages can't load HTTP iframes) so the
      // target origin is registered with the stub before UAA is called.
      // Blocked on confirming whether localhost:3000 is a registered redirect_uri
      // in UAA stg for the Amplify client_id, and settling on an opt-in signal
      // (env var vs. host config flag) rather than inferring from redirect_uri.

      return url;
    }
    return authURL;
  }, [accessToken, globalAuthPath, dm, login, sessionMismatch, setSharedSessionState]);

  return authURL;
};

export default useAuthURL;
